import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Link from "@mui/material/Link";
import MuiAlert from "@mui/material/Alert";
import { Breadcrumbs, Typography, Snackbar } from "@mui/material";

import { DIRECTED_AT } from "../../constants/constants";

import {
  getFullName,
  isEmpty as isStringEmpty,
} from "../../../../utils/string";

import "./RecognitionSearchEmployee.css";

import RecognitionBanner from "../RecognitionBanner";
import RecognitionDialog from "../RecognitionDialog";
import RecognitionSearchBar from "../RecognitionSearchBar";

import { ERROR_CODE } from "../../../../constants/constants";

import { isEmpty as isArrayEmpty } from "../../../../utils/array";
import { isEmpty as isObjectEmpty } from "../../../../utils/object";

import {
  fetchTeammates,
  fetchCategories,
  postRecognition,
  fetchDirectReports,
} from "../../services/recognition";

export default function RecognitionSearchEmployee() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [
    hasTriedRecognitionFormSubmission,
    setHasTriedRecognitionFormSubmission,
  ] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [isFetchingAgencyCategories, setIsFetchingAgencyCategories] =
    useState(false);
  const [isSubmittingRecognitionForm, setIsSubmittingRecognitionForm] =
    useState(false);
  const [isFetchingTeammates, setIsFetchingTeammates] = useState(false);
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [isSuccessMessageShown, setIsSuccessMessageShown] = useState(false);
  const [isFetchingDirectReports, setIsFetchingDirectReports] = useState(false);
  const [directedAt, setDirectedAt] = useState(searchParams.get("directed-at"));
  const [isRecognitionDialogOpen, setIsRecognitionDialogOpen] = useState(false);

  const [directedAtForm, setDirectedAtForm] = useState({
    directedAt: DIRECTED_AT[directedAt].value,
    category: "",
    reason: "",
  });

  useEffect(() => {
    if (isStringEmpty(directedAt)) {
      navigate("/recognition/direct-at");
    }

    if (directedAt === DIRECTED_AT["My Teammates"]?.label) {
      handleFetchTeammates();
    }

    if (directedAt === DIRECTED_AT["My Direct Reports"]?.label) {
      handleFetchDirectReports();
    }
  }, []);

  /**
   * Filters the list of employees as per the search text in the search bar.
   *
   * @param {Array} employees
   * @param {String} searchText
   *
   * @returns {Array}
   */
  const getFilteredEmployees = (employees = [], searchText = "") => {
    if (isArrayEmpty(employees)) {
      return [];
    }

    if (isStringEmpty(searchText)) {
      return employees;
    }

    return employees.filter((employee) => {
      const fullName = getFullName(
        employee?.caregiver?.first_name,
        employee?.caregiver?.last_name
      );

      return fullName?.toLowerCase()?.includes(searchText.toLowerCase());
    });
  };

  /**
   * Fetches the Direct Reports with matching search text via API call.
   *
   * @returns {Promise}
   */
  const fetchDirectReportsAsync = async (searchText = "") => {
    try {
      return await fetchDirectReports({ key: searchText });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/recognition");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Fetches the Teammates with matching search text via API call.
   *
   * @returns {Promise}
   */
  const fetchTeammatesAsync = async (searchText = "") => {
    try {
      return await fetchTeammates({ key: searchText });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/recognition");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Handles the fetching of the Teammates.
   */
  const handleFetchTeammates = async () => {
    setIsFetchingTeammates(true);
    const response = await fetchTeammatesAsync();

    setIsFetchingTeammates(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching My Teammates: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);

      return;
    }

    setEmployees(response?.result?.employees);
  };

  /**
   * Handles the fetching of the Direct Reports.
   */
  const handleFetchDirectReports = async () => {
    setIsFetchingDirectReports(true);
    const response = await fetchDirectReportsAsync();

    setIsFetchingDirectReports(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching My Direct Reports: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);

      return;
    }

    setEmployees(response?.result?.employees);
  };

  // /**
  //  * Handles the fetching of employees as per the search text in the search bar.
  //  *
  //  * @param {*} String
  //  */
  // const handleFetchEmployees = async (searchText = "") => {
  //   setIsFetchingEmployees(true);
  //   let response;

  //   if (directedAt === DIRECTED_AT["My Direct Reports"]?.label) {
  //     response = await fetchDirectReportsAsync(searchText);
  //   }

  //   if (directedAt === DIRECTED_AT["My Teammates"]?.label) {
  //     response = await fetchTeammatesAsync(searchText);
  //   }

  //   setIsFetchingEmployees(false);
  // };

  // /**
  //  * Debounced users fetching on typing user search value.
  //  */
  // const debouncedHandleFetchEmployees = useCallback(
  //   debounce(handleFetchEmployees, 300),
  //   []
  // );

  /**
   * Closes the dialog to provide Recognition.
   */
  const closeRecognitionDialog = () => {
    setIsRecognitionDialogOpen(false);
  };

  /**
   * Opens the dialog to provide Recognition.
   */
  const openRecognitionDialog = () => {
    setIsRecognitionDialogOpen(true);
  };

  /**
   * Method to handle the closing of the Recognition dialog.
   */
  const handleRecognitionDialogClose = () => {
    setHasTriedRecognitionFormSubmission(false);
    setDirectedAtForm({
      directedAt: DIRECTED_AT[directedAt].value,
      category: categories?.[0],
      reason: "",
    });
    setSelectedEmployee({});
    closeRecognitionDialog();
  };

  /**
   * Handles the change in the form field of the Recognition Form.
   */
  const handleRecognitionFormFieldChange = (key, value) => {
    setDirectedAtForm({
      ...directedAtForm,
      [key]: value,
    });
  };

  /**
   * Fetches the Agency Categories via API call.
   *
   * @returns {Promise}
   */
  const fetchAgencyCategories = async () => {
    try {
      return await fetchCategories();
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/recognition");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Handles the fetching of the logged in user's Agency Category.
   */
  const handleFetchAgencyCategories = async () => {
    setIsFetchingAgencyCategories(true);
    const response = await fetchAgencyCategories();

    setIsFetchingAgencyCategories(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching Categories: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setCategories([]);

      return;
    }

    setCategories(response?.result?.categories);
    handleRecognitionFormFieldChange(
      "category",
      response?.result?.categories?.[0]
    );
  };

  /**
   * Handles when clicked on the employee from search result.
   *
   * @param {Number} id
   */
  const handleResultClick = (id) => {
    if (isArrayEmpty(employees)) {
      return;
    }

    const selectedEmployee = employees.find((employee) => employee?.id === id);

    if (isObjectEmpty(selectedEmployee)) {
      return;
    }

    setSelectedEmployee(selectedEmployee);
    openRecognitionDialog();

    if (isArrayEmpty(categories)) {
      handleFetchAgencyCategories();
    }
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  /**
   * Renders the success snackbar for the Dashboard page.
   *
   * @returns {Component}
   */
  const renderSuccessSnackbar = () => (
    <Snackbar
      open={isSuccessMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsSuccessMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsSuccessMessageShown(false)}
        severity="success"
        sx={{ width: "100%" }}
      >
        {successMessage}
      </MuiAlert>
    </Snackbar>
  );

  /**
   * Method to check if the Recognition Dialog Form Fields are valid.
   *
   * @returns {Boolean}
   */
  const isRecognitionFormValid = () => {
    return (
      !isStringEmpty(directedAtForm?.directedAt) &&
      !isStringEmpty(directedAtForm?.category) &&
      !isStringEmpty(directedAtForm?.reason)
    );
  };

  /**
   * Submits the Recognition via API calls.
   *
   * @param {Object} payload
   * @returns {Promise}
   */
  const submitRecognitionFormAsync = async (payload = {}) => {
    try {
      return await postRecognition(payload);
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/recognition");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Handles the submission of the Recognition form.
   *
   * @returns {Promise}
   */
  const handleRecognitionSubmit = async () => {
    setHasTriedRecognitionFormSubmission(true);

    if (!isRecognitionFormValid()) {
      return;
    }

    setIsSubmittingRecognitionForm(true);
    const response = await submitRecognitionFormAsync({
      recognized_id: selectedEmployee?.id,
      reason: directedAtForm?.reason,
      directed_at: directedAtForm?.directedAt,
      category: directedAtForm?.category,
    });

    setIsSubmittingRecognitionForm(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while submitting Recognition: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);

      return;
    }

    setSuccessMessage(`Successfully submitted the Recognition.`);
    setIsSuccessMessageShown(true);
    handleRecognitionDialogClose();
  };

  return (
    <>
      <div className="search-employees-main-outer-wrapper">
        <RecognitionBanner />

        <div className="search-employees-inner-wrapper">
          <div className="breadcrumbs-wrapper">
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                color="primary"
                href="/recognition/direct-at"
              >
                Recognition
              </Link>

              <Typography
                color="text.primary"
                sx={{
                  fontWeight: "var(--fontWeight-bold)",
                }}
              >
                {DIRECTED_AT[directedAt]?.label}
              </Typography>
            </Breadcrumbs>
          </div>

          <div className="info-wrapper">
            <h2>Provide Your Recognition.</h2>
            <p>To whom do you want to direct your Recognition?</p>
          </div>

          <div
            className="search-bar-wrapper"
            id="recognition-search-bar-wrapper"
          >
            <RecognitionSearchBar
              results={employees}
              onFilter={getFilteredEmployees}
              onResultClick={handleResultClick}
              placeholder={`Search ${directedAt}`}
              isLoading={isFetchingTeammates || isFetchingDirectReports}
            />
          </div>
        </div>
      </div>
      <RecognitionDialog
        categories={categories}
        formData={directedAtForm}
        isLoadingDirectedAt={false}
        directedAt={selectedEmployee}
        open={isRecognitionDialogOpen}
        onSubmit={handleRecognitionSubmit}
        onClose={handleRecognitionDialogClose}
        isSubmitting={isSubmittingRecognitionForm}
        isFetchingCategories={isFetchingAgencyCategories}
        changeFormData={handleRecognitionFormFieldChange}
        hasTriedFormSubmission={hasTriedRecognitionFormSubmission}
      />
      {renderErrorSnackbar()}
      {renderSuccessSnackbar()}
    </>
  );
}
